import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Grid3X3, Plus, Trash2, RotateCcw, Layout, Columns, Rows } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "2D Layout System",
        icon: "🔲",
        content: (
            <div className="space-y-6">
                <div className="bg-pink-50 p-6 rounded-2xl border-2 border-pink-200">
                    <h3 className="font-bold text-xl text-pink-800 mb-3">Both Rows AND Columns</h3>
                    <p className="text-slate-700 mb-4">
                        Unlike Flexbox (which is 1D), <strong>CSS Grid</strong> lets you control layout in 2 dimensions at once.
                    </p>
                    <p className="text-slate-700">
                        It's like an Excel spreadsheet or a blueprint. You define the grid lines, and then place items into the cells.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Defining the Grid",
        icon: "📐",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-900 text-green-400 p-4 rounded-xl font-mono text-sm">
                    <div>.container {'{'}</div>
                    <div className="pl-4">display: grid;</div>
                    <div className="pl-4">grid-template-columns: <span className="text-yellow-400">1fr 1fr 1fr</span>;</div>
                    <div className="pl-4">gap: <span className="text-purple-400">20px</span>;</div>
                    <div>{'}'}</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-600 text-sm">
                        <code className="bg-slate-100 px-1 rounded font-bold">1fr</code> means "1 fraction" of the available space.
                        Three 1fr columns divide the space equally into thirds!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Spanning Cells",
        icon: "↔️",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Breaking the Grid</h3>
                    <p className="text-slate-700 mb-4">
                        Items don't have to stay in one small cell. They can stretch!
                    </p>
                    <div className="bg-white p-3 rounded-lg border border-blue-100 font-mono text-sm text-slate-600">
                        grid-column: span 2; <span className="text-slate-400">// Takes up 2 columns width</span>
                        <br />
                        grid-row: span 3; <span className="text-slate-400">// Takes up 3 rows height</span>
                    </div>
                </div>
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-bold text-green-800 mb-2">🎮 Mission: Build a Dashboard</h4>
                    <p className="text-green-700 text-sm">
                        Use the grid editor to create a layout with at least <strong>5 items</strong> (Header, Sidebar, Widget, etc.)
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
interface GridItem {
    id: number;
    colSpan: number;
    rowSpan: number;
    color: string;
    label: string;
}

const COLORS = [
    'bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500',
    'bg-yellow-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500'
];

const LABELS = ['Header', 'Nav', 'Main', 'Sidebar', 'Footer', 'Card', 'Widget', 'Hero'];

const CssGame = ({ onComplete }: { onComplete: () => void }) => {
    const [columns, setColumns] = useState(3);
    const [rows, setRows] = useState(3);
    const [gap, setGap] = useState(16);
    const [items, setItems] = useState<GridItem[]>([
        { id: 1, colSpan: 3, rowSpan: 1, color: 'bg-blue-500', label: 'Header' },
        { id: 2, colSpan: 1, rowSpan: 2, color: 'bg-green-500', label: 'Nav' },
        { id: 3, colSpan: 2, rowSpan: 2, color: 'bg-purple-500', label: 'Main' },
    ]);

    useEffect(() => {
        if (items.length >= 5) {
            onComplete();
        }
    }, [items]);

    const addItem = () => {
        const newItem: GridItem = {
            id: Date.now(),
            colSpan: 1,
            rowSpan: 1,
            color: COLORS[items.length % COLORS.length],
            label: LABELS[items.length % LABELS.length]
        };
        setItems([...items, newItem]);
    };

    const removeItem = (id: number) => {
        setItems(items.filter(i => i.id !== id));
    };

    const updateItem = (id: number, updates: Partial<GridItem>) => {
        setItems(items.map(i => i.id === id ? { ...i, ...updates } : i));
    };

    const reset = () => {
        setItems([
            { id: 1, colSpan: 3, rowSpan: 1, color: 'bg-blue-500', label: 'Header' },
            { id: 2, colSpan: 1, rowSpan: 2, color: 'bg-green-500', label: 'Nav' },
            { id: 3, colSpan: 2, rowSpan: 2, color: 'bg-purple-500', label: 'Main' },
        ]);
        setColumns(3);
        setRows(3);
        setGap(16);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Controls */}
            <div className="space-y-6">
                {/* Grid Settings */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-slate-100">
                    <h2 className="font-bold text-slate-700 mb-4">Grid Container</h2>

                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-bold text-slate-600 mb-1 block">
                                Columns: {columns}
                            </label>
                            <input
                                type="range" min="1" max="6" value={columns}
                                onChange={e => setColumns(Number(e.target.value))}
                                className="w-full accent-pink-500"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-slate-600 mb-1 block">
                                Rows: {rows}
                            </label>
                            <input
                                type="range" min="1" max="6" value={rows}
                                onChange={e => setRows(Number(e.target.value))}
                                className="w-full accent-pink-500"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-slate-600 mb-1 block">
                                Gap: {gap}px
                            </label>
                            <input
                                type="range" min="0" max="32" value={gap}
                                onChange={e => setGap(Number(e.target.value))}
                                className="w-full accent-pink-500"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={addItem}
                            className="flex-1 bg-pink-500 hover:bg-pink-400 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-pink-200"
                        >
                            <Plus size={18} /> Add Item
                        </button>
                        <button
                            onClick={reset}
                            className="px-4 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-xl transition-colors"
                        >
                            <RotateCcw size={18} />
                        </button>
                    </div>
                </div>

                {/* Item List */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-slate-100 max-h-80 overflow-y-auto">
                    <h2 className="font-bold text-slate-700 mb-4">Grid Items ({items.length}/5)</h2>

                    <div className="space-y-3">
                        {items.map((item, idx) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-slate-50 rounded-xl p-3 border border-slate-100"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-4 h-4 rounded ${item.color}`} />
                                        <span className="font-bold text-sm text-slate-700">{item.label}</span>
                                    </div>
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-red-400 hover:text-red-600 p-1"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <div className="flex gap-2 text-xs">
                                    <div className="flex-1">
                                        <label className="text-slate-500">col-span</label>
                                        <select
                                            value={item.colSpan}
                                            onChange={e => updateItem(item.id, { colSpan: Number(e.target.value) })}
                                            className="w-full bg-white border border-slate-200 rounded px-2 py-1 mt-1"
                                        >
                                            {[1, 2, 3, 4, 5, 6].map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-slate-500">row-span</label>
                                        <select
                                            value={item.rowSpan}
                                            onChange={e => updateItem(item.id, { rowSpan: Number(e.target.value) })}
                                            className="w-full bg-white border border-slate-200 rounded px-2 py-1 mt-1"
                                        >
                                            {[1, 2, 3, 4, 5, 6].map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Grid Preview */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-lg border-2 border-slate-100">
                <h2 className="font-bold text-slate-700 mb-4 flex items-center justify-between">
                    Preview
                    <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">
                        {columns} cols x {rows} rows
                    </span>
                </h2>

                <div
                    className="bg-slate-100 rounded-2xl p-4 min-h-[400px] border-4 border-dashed border-slate-200"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridTemplateRows: `repeat(${rows}, 80px)`,
                        gap: `${gap}px`
                    }}
                >
                    {items.map(item => (
                        <motion.div
                            key={item.id}
                            layout
                            className={`${item.color} rounded-xl flex items-center justify-center text-white font-bold shadow-lg relative group`}
                            style={{
                                gridColumn: `span ${Math.min(item.colSpan, columns)}`,
                                gridRow: `span ${Math.min(item.rowSpan, rows)}`
                            }}
                        >
                            {item.label}
                            <div className="absolute inset-0 border-2 border-white/50 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity pointer-events-none" />
                        </motion.div>
                    ))}
                </div>

                {/* Generated CSS */}
                <div className="mt-6 bg-slate-900 rounded-xl p-4 font-mono text-sm text-green-400">
                    <div className="text-slate-500 mb-2">/* Generated CSS */</div>
                    <div>.container {'{'}</div>
                    <div className="pl-4">display: grid;</div>
                    <div className="pl-4">grid-template-columns: <span className="text-yellow-400">repeat({columns}, 1fr)</span>;</div>
                    <div className="pl-4">grid-template-rows: <span className="text-yellow-400">repeat({rows}, 80px)</span>;</div>
                    <div className="pl-4">gap: <span className="text-pink-400">{gap}px</span>;</div>
                    <div>{'}'}</div>
                    {items.length > 0 && (
                        <>
                            <div className="mt-2">.item-1 {'{'}</div>
                            <div className="pl-4">grid-column: <span className="text-blue-400">span {items[0]?.colSpan}</span>;</div>
                            <div className="pl-4">grid-row: <span className="text-blue-400">span {items[0]?.rowSpan}</span>;</div>
                            <div>{'}'}</div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const CssGrid = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('css-grid', 100);
    };

    return (
        <LessonWrapper
            moduleName="CSS Grid Layout"
            moduleEmoji="🔲"
            moduleColor="pink"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Grid Editor"
        >
            <CssGame onComplete={handleComplete} />
            {completed && (
                <div className="mt-8">
                    <Link to="/games/grid-invaders" className="w-full py-4 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-bold flex items-center justify-center gap-2 transition-transform hover:scale-105 border-b-4 border-pink-800 shadow-lg">
                        👾 Play "CSS Grid Invaders" Arcade
                    </Link>
                    <p className="text-center text-xs text-pink-400 mt-2 font-bold uppercase">Game Unlocked!</p>
                </div>
            )}
        </LessonWrapper>
    );
};

export default CssGrid;
